import { io } from "socket.io-client"
import { ref } from "vue"
import { useLocationStore } from "@/stores/location"
let watchId = null
let socket = null
let activeRequestId = null // Track active CityXpress request ID
let isTrackingEnabled = false // Track if location tracking should be active
let heartbeatTimer = null
let lastKnownCoords = null
let pendingCityXpressAvailability = null
const HEARTBEAT_INTERVAL_MS = 10000 // Keep online heartbeat alive even when stationary

import { useAuthStore } from "@/stores/auth"

export function useLocationTracker() {
  const authStore = useAuthStore()
  const locationStore = useLocationStore() // 'granted', 'denied', 'prompt'

  const checkLocationPermission = async () => {
    try {
      if (!("geolocation" in navigator)) {
        return {
          granted: false,
          error: "Geolocation is not supported by your browser",
        }
      }

      // If Permissions API is not available
      if (!("permissions" in navigator)) {
        return tryGeolocationFallback()
      }

      const permission = await navigator.permissions.query({ name: "geolocation" })
      locationStore.permissionStatus = permission.state

      permission.addEventListener("change", () => {
        locationStore.permissionStatus = permission.state
      })

      if (permission.state === "granted") {
        return { granted: true, state: permission.state }
      }

      if (permission.state === "denied") {
        return { granted: false, state: permission.state, error: "Location access denied" }
      }

      // 'prompt' or unknown state: try to request location silently
      return await tryGeolocationFallback()
    } catch (error) {
      console.error("Error checking location permission:", error)
      return {
        granted: false,
        error: error.message,
      }
    }
  }

  const tryGeolocationFallback = async () => {
    try {
      await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 5000,
          maximumAge: 0,
        })
      })
      locationStore.permissionStatus = "granted"
      return { granted: true }
    } catch (error) {
      locationStore.permissionStatus = "denied"
      return {
        granted: false,
        error: "Location permission denied or unavailable",
      }
    }
  }

  const requestLocationPermission = async () => {
    try {
      const result = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            locationStore.permissionStatus = "granted"
            resolve({
              granted: true,
              position,
            })
          },
          (error) => {
            locationStore.permissionStatus = "denied"
            reject({
              granted: false,
              error: error.message,
            })
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          }
        )
      })
      return result
    } catch (error) {
      console.error("Error requesting location permission:", error)
      return {
        granted: false,
        error: error.message,
      }
    }
  }

  const startTracking = async () => {
    if (watchId !== null) {
      isTrackingEnabled = true
      startHeartbeatTimer()
      return { granted: true }
    }
    if (!authStore.user?.id) return { granted: false, error: "User not authenticated." }

    const permissionResult = await checkLocationPermission()
    if (!permissionResult.granted) return permissionResult
    console.log("[LocationTracker] Permission granted. Proceeding to connect WebSocket...")

    isTrackingEnabled = true
    if (!socket) connectToWebSocket()

    return { granted: true }
  }

  const stopTracking = () => {
    isTrackingEnabled = false
    console.log("[LocationTracker] Stopping location tracking")

    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId)
      watchId = null
    }

    if (socket) {
      socket.close()
      socket = null
    }

    if (heartbeatTimer) {
      clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }

    lastKnownCoords = null
  }

  const connectToWebSocket = () => {
    const normalizedRole = String(authStore.user?.role || "viewer").toLowerCase()
    socket = io(useRuntimeConfig().public.trackingServiceBase, {
      query: {
        role: normalizedRole,
        id: authStore.user?.id || "",
      },
    })

    socket.on("connect", () => {
      console.log("[Socket.IO] Connected to tracking service")
      if (pendingCityXpressAvailability && authStore.user?.id) {
        const vehicleTypeId =
          authStore.user?.vehicle?.vehicle_type_id ||
          authStore.user?.driver?.vehicle?.vehicle_type_id ||
          authStore.user?.vehicle_type_id ||
          null
        const vehicleTypeCode =
          authStore.user?.vehicle?.vehicle_type?.code ||
          authStore.user?.driver?.vehicle?.vehicle_type?.code ||
          null

        const payload = {
          driverId: authStore.user.id,
          status: pendingCityXpressAvailability,
        }

        if (vehicleTypeId) payload.vehicleTypeId = vehicleTypeId
        if (vehicleTypeCode) payload.vehicleType = vehicleTypeCode

        socket.emit("cityxpress:set-availability", payload)
      }

      watchId = navigator.geolocation.watchPosition(
        ({ coords }) => {
          // Only send if tracking is enabled (driver is online or on trip)
          if (!isTrackingEnabled) {
            return
          }

          const { latitude, longitude } = coords
          lastKnownCoords = { latitude, longitude }

          // Check if driver is online or has active trip
          const shouldSend = checkShouldSendLocation()
          if (shouldSend) {
            sendViaWebSocket({ latitude, longitude, requestId: activeRequestId })
          }
        },
        (err) => {
          console.error("[LocationTracker] Geolocation error:", err.message)
          // Only retry if tracking is enabled
          if (isTrackingEnabled) {
            navigator.geolocation.getCurrentPosition(
              ({ coords }) => {
                const { latitude, longitude } = coords
                lastKnownCoords = { latitude, longitude }
                if (checkShouldSendLocation()) {
                  sendViaWebSocket({ latitude, longitude, requestId: activeRequestId })
                }
              },
              (error) => {
                console.error("[LocationTracker] Fallback geolocation failed:", error.message)
              },
              { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
            )
          }
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 60000 }
      )

      startHeartbeatTimer()
    })

    socket.on("disconnect", () => {
      console.log("[Socket.IO] Connection closed")
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer)
        heartbeatTimer = null
      }
    })

    socket.on("connect_error", (err) => {
      console.error("[Socket.IO] Error:", err.message)
    })
  }

  const emitCityXpressAvailability = (status) => {
    const normalizedStatus = status === "available" ? "available" : "unavailable"
    pendingCityXpressAvailability = normalizedStatus
    const vehicleTypeId =
      authStore.user?.vehicle?.vehicle_type_id ||
      authStore.user?.driver?.vehicle?.vehicle_type_id ||
      authStore.user?.vehicle_type_id ||
      null
    const vehicleTypeCode =
      authStore.user?.vehicle?.vehicle_type?.code ||
      authStore.user?.driver?.vehicle?.vehicle_type?.code ||
      null

    if (socket && socket.connected && authStore.user?.id) {
      const payload = {
        driverId: authStore.user.id,
        status: normalizedStatus,
      }

      if (vehicleTypeId) payload.vehicleTypeId = vehicleTypeId
      if (vehicleTypeCode) payload.vehicleType = vehicleTypeCode

      socket.emit("cityxpress:set-availability", payload)
    }
  }

  const startHeartbeatTimer = () => {
    if (heartbeatTimer) return

    heartbeatTimer = setInterval(() => {
      if (!isTrackingEnabled || !checkShouldSendLocation()) {
        return
      }

      if (!lastKnownCoords) {
        return
      }

      sendViaWebSocket({
        latitude: lastKnownCoords.latitude,
        longitude: lastKnownCoords.longitude,
        requestId: activeRequestId,
      })
    }, HEARTBEAT_INTERVAL_MS)
  }

  // Check if location should be sent (driver is online or on active trip)
  const checkShouldSendLocation = () => {
    // If on active CityXpress trip, always send location
    if (activeRequestId) {
      return true
    }

    // General tracking remains enabled by tracking state (e.g. shipment tracking).
    return isTrackingEnabled
  }

  const sendViaWebSocket = ({ latitude, longitude, requestId = null }) => {
    if (socket && socket.connected && authStore.user?.id) {
      const payload = {
        id: authStore.user.id,
        role: String(authStore.user.role || "viewer").toLowerCase(),
        latitude,
        longitude,
        type: requestId ? "cityxpress" : "driver", // Use cityxpress type when on trip
      }

      // Include requestId for CityXpress trips
      if (requestId) {
        payload.requestId = requestId
      }

      // Debug logging
      const timestamp = new Date().toISOString()
      console.log(`[LocationTracker] 📍 Heartbeat sent at ${timestamp}:`, {
        driverId: authStore.user.id,
        location: { lat: latitude, lng: longitude },
        type: payload.type,
        requestId: requestId || "none",
      })

      socket.emit("location-update", payload)
    }
  }

  // Set active CityXpress request ID (for trip tracking)
  const setActiveRequestId = (requestId) => {
    activeRequestId = requestId
    console.log(`[LocationTracker] Set active CityXpress request ID: ${requestId}`)
  }

  // Clear active CityXpress request ID (when trip ends)
  const clearActiveRequestId = () => {
    activeRequestId = null
    console.log("[LocationTracker] Cleared active CityXpress request ID")
  }

  return {
    startTracking,
    stopTracking,
    checkLocationPermission,
    requestLocationPermission,
    setActiveRequestId,
    clearActiveRequestId,
    emitCityXpressAvailability,
  }
}
